import { supabase } from '../supabase';
import { useAuthStore } from '../../stores/authStore';

// Error handling utility
async function extractInvokeErrorMessage(error: unknown): Promise<string | null> {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const fnError = error as { name?: string; context?: unknown; message?: string };

  if (fnError.name === 'FunctionsHttpError' && fnError.context instanceof Response) {
    try {
      const body = await fnError.context.clone().json();
      if (typeof body?.error === 'string') {
        return body.error;
      }
      if (typeof body?.message === 'string') {
        return body.message;
      }
    } catch {
      try {
        const text = await fnError.context.clone().text();
        if (text) {
          return text;
        }
      } catch {
        // ignore parse failures
      }
    }
  }

  return null;
}

async function throwIfInvokeFailed(data: unknown, error: unknown): Promise<void> {
  if (data && typeof data === 'object' && 'error' in data) {
    const message = (data as { error?: string }).error;
    if (message) {
      throw new Error(message);
    }
  }
  if (error) {
    const message = await extractInvokeErrorMessage(error);
    if (message) {
      throw new Error(message);
    }
    handleApiError(error);
  }
}

function handleApiError(error: any): never {
  console.error('Stripe API Error:', error);
  
  // Handle Supabase FunctionsHttpError specifically first
  if (error?.name === 'FunctionsHttpError' || error?.constructor?.name === 'FunctionsHttpError') {
    try {
      // Try to extract the response body from the error
      const responseText = error.context?.body || error.message;
      
      // Try to parse as JSON if it looks like JSON
      if (typeof responseText === 'string' && 
          (responseText.startsWith('{') || responseText.startsWith('['))) {
        try {
          const parsedBody = JSON.parse(responseText);
          if (parsedBody?.error) {
            throw new Error(parsedBody.error);
          }
          if (parsedBody?.message) {
            throw new Error(parsedBody.message);
          }
        } catch (jsonError) {
          // If JSON parsing fails, continue with other error handling
          console.warn('Failed to parse response as JSON:', jsonError);
        }
      }
    } catch (parseError) {
      // If JSON parsing fails, continue with other error handling
      console.warn('Failed to parse FunctionsHttpError message as JSON:', parseError);
    }
  }
  
  // Try to extract detailed error information from Supabase Edge Function response
  if (error?.context?.body) {
    try {
      const errorBody = typeof error.context.body === 'string' 
        ? JSON.parse(error.context.body) 
        : error.context.body;
      
      if (errorBody?.error) {
        throw new Error(errorBody.error);
      }
      if (errorBody?.message) {
        throw new Error(errorBody.message);
      }
    } catch (parseError) {
      // If parsing fails, continue with other error handling
      console.warn('Failed to parse error context body:', parseError);
    }
  }
  
  // Try to extract error details from the details property
  if (error?.details) {
    try {
      const errorDetails = typeof error.details === 'string' 
        ? JSON.parse(error.details) 
        : error.details;
      
      if (errorDetails?.error) {
        throw new Error(errorDetails.error);
      }
      if (errorDetails?.message) {
        throw new Error(errorDetails.message);
      }
    } catch (parseError) {
      // If parsing fails, continue with other error handling
      console.warn('Failed to parse error details:', parseError);
    }
  }
  
  // Check for status code and try to provide more context
  if (error?.status) {
    const statusMessage = `HTTP ${error.status}`;
    if (error.statusText) {
      throw new Error(`${statusMessage}: ${error.statusText}`);
    }
    throw new Error(statusMessage);
  }
  
  // Use the error message if it's not the generic Edge Function error
  if (error?.message && 
      error.message !== 'Edge Function returned a non-2xx status code' &&
      error.message !== 'FunctionsHttpError' &&
      !error.message.includes('status code 500')) {
    throw new Error(error.message);
  }
  
  if (typeof error === 'string') {
    throw new Error(error);
  }
  
  throw new Error('An unexpected error occurred. Please check the console for more details.');
}

// Cancel subscription
export async function cancelSubscription(subscriptionId: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('cancel-subscription', {
      body: {
        subscriptionId,
      },
    });

    await throwIfInvokeFailed(data, error);

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    handleApiError(error);
  }
}

// Update subscription without proration
export async function updateSubscription(
  subscriptionId: string,
  newPlan: string,
  newPriceId: string,
  isUpgrade: boolean,
  userId: string
): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('update-subscription', {
      body: {
        subscriptionId,
        newPlan,
        newPriceId,
        isUpgrade,
        userId,
      },
    });

    await throwIfInvokeFailed(data, error);

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    handleApiError(error);
  }
}

// Create checkout session for subscription or one-time payment
export async function createCheckoutSession(
  priceId: string,
  mode: 'payment' | 'subscription', 
  userId: string,
  metadata?: Record<string, string>,
  quantity: number = 1, 
  successUrl?: string, 
  cancelUrl?: string
) {
  try {
    // Get the current user's email from auth store
    const user = useAuthStore.getState().user;
    if (!user?.email) {
      throw new Error('User email is required but not available');
    }

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId,
        mode,
        userId,
        customerEmail: user.email, // Explicitly pass the email to avoid user lookup in Edge Function
        metadata,
        quantity, 
        successUrl, 
        cancelUrl,
      },
    });

    await throwIfInvokeFailed(data, error);

    if (!data || !data.url) {
      throw new Error('No checkout URL returned');
    }

    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    handleApiError(error);
  }
}

// Create subscription using Supabase Edge Function
export async function createSubscription(
  priceId: string,
  customerId?: string,
  customerEmail?: string,
  metadata?: Record<string, string>,
  paymentMethodId?: string
) {
  try {
    console.log('Creating subscription with:', { priceId, customerId, customerEmail, metadata, paymentMethodId });
    
    const { data, error } = await supabase.functions.invoke('create-subscription', {
      body: {
        priceId,
        customerId,
        customerEmail,
        metadata,
        paymentMethodId,
      },
    });

    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(error.message || 'Failed to create subscription');
    }

    if (!data) {
      throw new Error('No data returned from subscription creation');
    }

    console.log('Subscription created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error in createSubscription:', error);
    handleApiError(error);
  }
}

// Get customer portal URL
export async function createCustomerPortalSession(userId: string, returnUrl?: string) {
  try {
    const { data, error } = await supabase.functions.invoke('create-customer-portal', {
      body: {
        userId,
        returnUrl,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to create customer portal session');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// Add payment method to customer
export async function addPaymentMethod(
  userId: string,
  paymentMethodId: string,
  setAsDefault: boolean = false
) {
  try {
    const { data, error } = await supabase.functions.invoke('add-payment-method', {
      body: {
        userId,
        paymentMethodId,
        setAsDefault,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to add payment method');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// Create payment intent for one-time charges
export async function createPaymentIntent(
  amount: number,
  currency: string = 'jpy', 
  userId?: string,
  metadata?: Record<string, string>,
  productId?: string
) {
  try {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId: productId || '',
        mode: 'payment',
        userId,
        metadata,
        amount,
        currency,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to create payment intent');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// Delete payment method
export async function deletePaymentMethod(paymentMethodId: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('delete-payment-method', {
      body: {
        paymentMethodId,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to delete payment method');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// Set default payment method for customer
export async function setDefaultPaymentMethod(userId: string, paymentMethodId: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('set-default-payment-method', {
      body: {
        userId,
        paymentMethodId,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to set default payment method');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// Get payment methods for a customer
export async function getPaymentMethods(userId: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('list-payment-methods', {
      body: {
        userId,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to get payment methods');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// Get invoices for a customer
export async function getInvoices(userId: string, limit?: number): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('list-invoices', {
      body: {
        userId,
        limit,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to get invoices');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}

// get upcoming invoice for a customer
export async function getUpcomingInvoice(userId: string): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('get-upcoming-invoice', {
      body: {
        userId,
      },
    });

    if (error) {
      throw new Error(error.message || 'Failed to get upcoming invoice');
    }

    return data;
  } catch (error) {
    handleApiError(error);
  }
}